import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { Col, Row } from 'antd';

import Flat from '../../backend/s3/flat';
import { computeMapAreas } from '../../backend/geo';
import Office from '../../backend/s3/office';
import Testimonial from '../../backend/testimonial';
import { getLastPosts } from '../../backend/wp';
import { SEARCH_QUERY_GENERIC, SITE_URL } from '../../common/consts';
import { IContact } from '../../common/model/mailchimp/contact.model';
import { IFlat } from '../../common/model/flat.model';
import { ISuggestion } from '../../common/model/suggestion.model';
import { IMyPost } from '../../common/model/wp/post.model';
import { IMapArea } from '../../common/model/mapArea.model';
import { IOffice } from '../../common/model/office.model';
import { ITestimonial } from '../../common/model/testimonial.model';
import useI18n from '../../common/hooks/useI18n';
import { computeResults } from '../../common/helpers/searchService.utils';
import * as suggestionUtils from '../../common/helpers/suggestion.utils';
import { getSellHouseOptionsBySheetName } from '../../common/helpers/i18n.utils';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../../common/helpers/buyHouseOption.utils';
import {
  SearchOption,
  filterByText,
} from '../../common/helpers/searchOption.utils';
import { slugifySearchQuery } from '../../common/helpers/searchQuery.utils';
import { deserializeMultiple } from '../../common/helpers/serialization';
import { generateHomePageStructuredData } from '../../common/helpers/structuredData.utils';
import useMailchimpService from '../../common/hooks/mailchimpService';
import useSearchService from '../../common/hooks/useSearchService';

import {
  BannerSection,
  BlogShowcase,
  HierarchicalMap,
  NewsletterSection,
  PlaceSuggestions,
} from '../../components/home';
import { LinksGrid, LinksGridProvider } from '../../components/linksGrid';
import {
  FlatsDisplay,
  Footer,
  Header,
  Head,
  SearchHero,
  Testimonials,
} from '../../components/shared';

type StaticProps = {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  query: string;
  serializedFlats: string;
  serializedSearchOptions: string;
  lastPosts: IMyPost[];
  mapAreas: IMapArea[];
  suggestions: ISuggestion[];
  searchOptionInPath: string;
  testimonials: ITestimonial[];
};

type StaticParams = {
  query: string;
};

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
`;

const HierarchicalMapContainer = styled.div`
  margin-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  padding-top: 4.5rem;
  padding-bottom: 48px;

  @media ${(props) => props.theme.breakpoints.smd} {
    padding-top: 2rem;
  }
`;

const HierarchicalMapRow = styled(Row)`
  align-items: stretch;
`;

const GreyBackgroundContainer = styled.div`
  background-color: ${(props) => props.theme.colors.grey};
`;

const LinksGridContainer = styled.div`
  padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-top: 3rem;
`;

const HomePage: NextPage<Props> = ({
  offices,
  buyHouseOptions,
  query,
  serializedFlats,
  serializedSearchOptions,
  lastPosts,
  mapAreas,
  suggestions,
  searchOptionInPath,
  testimonials,
}: Props): JSX.Element => {
  const router = useRouter();
  const i18n = useI18n();
  const mailchimpService = useMailchimpService();

  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [recommendedFlats, setRecommendedFlats] = useState([] as IFlat[]);

  const flats = useMemo(() => deserializeMultiple<IFlat>(serializedFlats), [
    serializedFlats,
  ]);
  const searchOptions: SearchOption[] = useMemo(
    () => JSON.parse(serializedSearchOptions),
    [serializedSearchOptions]
  );
  const structuredData = useMemo(() => generateHomePageStructuredData(query), [
    query,
  ]);

  const searchServiceRef = useRef(useSearchService(flats));

  const onSubscribeButtonClick = useCallback(
    (email: string) => {
      const contact: IContact = { EMAIL: email, interests: [] };
      mailchimpService.subscribe(contact, router, i18n);
    },
    [i18n, mailchimpService, router]
  );

  useEffect(() => {
    setRecommendedFlats(searchServiceRef.current.getLastSearchsResults());
  }, []);

  const titleKey = 'metaTitle';
  const descriptionKey = 'metaDescription';
  const sellHouseTitleKey = 'sellHouseTitle';
  const sellHouseDescriptionKey = 'sellHouseDescription';
  const sellHouseButtonKey = 'sellHouseButton';
  const workWithUsTitleKey = 'workWithUsTitle';
  const workWithUsDescriptionKey = 'workWithUsDescription';
  const workWithUsButtonKey = 'workWithUsButton';
  const flatsDisplayTitleKey = 'flatsDisplayTitle';
  const magazineTitleKey = 'magazineTitle';
  const magazineDescriptionKey = 'magazineDescription';
  const magazineButtonKey = 'magazineButton';

  return (
    <Layout>
      <Head
        title={
          i18n.t(`dynamic.homePage.${query}.${titleKey}`) ||
          i18n.t(`homePage.${titleKey}`)
        }
        description={
          i18n.t(`dynamic.homePage.${query}.${descriptionKey}`) ||
          i18n.t(`homePage.${descriptionKey}`)
        }
        canonicalUrl={`${SITE_URL}${
          query === SEARCH_QUERY_GENERIC ? '' : '/' + query
        }`}
      >
        {structuredData ? (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(structuredData),
            }}
          />
        ) : null}
      </Head>

      <Header offices={offices} buyHouseOptions={buyHouseOptions} />

      <Content>
        <SearchHero
          autoCompleteValue={autoCompleteValue}
          autoCompleteOptions={filterByText(searchOptions, autoCompleteValue)}
          onAutoCompleteValueChange={setAutoCompleteValue}
          imageUrl={i18n.stagingT(`dynamic.homePage.${query}.heroImageUrl`)}
          imageObjectPosition="top right"
          i18nKeyPrefix={`dynamic.homePage.${query}.`}
        />

        <HierarchicalMapContainer>
          <HierarchicalMapRow justify="space-between" gutter={48}>
            <Col xs={24} lg={suggestions.length > 0 ? 16 : 24}>
              <HierarchicalMap query={query} mapAreas={mapAreas} />
            </Col>
            {suggestions.length > 0 && (
              <Col xs={24} lg={8}>
                <PlaceSuggestions query={query} suggestions={suggestions} />
              </Col>
            )}
          </HierarchicalMapRow>
        </HierarchicalMapContainer>

        <BannerSection
          title={
            i18n.t(`dynamic.homePage.${query}.${sellHouseTitleKey}`) ||
            i18n.t(`homePage.${sellHouseTitleKey}`)
          }
          subtitle={
            i18n.t(`dynamic.homePage.${query}.${sellHouseDescriptionKey}`) ||
            i18n.t(`homePage.${sellHouseDescriptionKey}`)
          }
          left={false}
          image="banner_sellhouse.png"
          buttonText={
            i18n.t(`dynamic.homePage.${query}.${sellHouseButtonKey}`) ||
            i18n.t(`homePage.${sellHouseButtonKey}`)
          }
          href="/vender-casa"
          isNextLink={true}
        />
        <BannerSection
          title={
            i18n.t(`dynamic.homePage.${query}.${workWithUsTitleKey}`) ||
            i18n.t(`homePage.${workWithUsTitleKey}`)
          }
          subtitle={
            i18n.t(`dynamic.homePage.${query}.${workWithUsDescriptionKey}`) ||
            i18n.t(`homePage.${workWithUsDescriptionKey}`)
          }
          left={true}
          image="banner_work_with_us.jpg"
          buttonText={
            i18n.t(`dynamic.homePage.${query}.${workWithUsButtonKey}`) ||
            i18n.t(`homePage.${workWithUsButtonKey}`)
          }
          href="/trabaja-con-nosotros"
          isNextLink={true}
        />

        <GreyBackgroundContainer>
          <FlatsDisplay
            flats={recommendedFlats}
            offices={offices}
            title={
              i18n.t(`dynamic.homePage.${query}.${flatsDisplayTitleKey}`, {
                text: searchOptionInPath,
              }) ||
              i18n.t(`homePage.${flatsDisplayTitleKey}`, {
                text: searchOptionInPath,
              })
            }
            arrows={true}
          />
        </GreyBackgroundContainer>

        <BlogShowcase query={query} lastPosts={lastPosts} />

        <GreyBackgroundContainer>
          <Testimonials testimonials={testimonials} />
        </GreyBackgroundContainer>

        <BannerSection
          title={
            i18n.t(`dynamic.homePage.${query}.${magazineTitleKey}`) ||
            i18n.t(`homePage.${magazineTitleKey}`)
          }
          subtitle={
            i18n.t(`dynamic.homePage.${query}.${magazineDescriptionKey}`) ||
            i18n.t(`homePage.${magazineDescriptionKey}`)
          }
          left={false}
          image="banner_magazine.jpg"
          buttonText={
            i18n.t(`dynamic.homePage.${query}.${magazineButtonKey}`) ||
            i18n.t(`homePage.${magazineButtonKey}`)
          }
          href="https://www.inmobiliarianucleo.com/blog/3d-flip-book/revista"
          isNextLink={false}
        />
        <NewsletterSection onSubscribeButtonClick={onSubscribeButtonClick} />

        <LinksGridProvider mode="home" flats={flats} offices={offices}>
          <LinksGridContainer>
            <LinksGrid />
          </LinksGridContainer>
        </LinksGridProvider>
      </Content>

      <Footer />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths<StaticParams> = async () => {
  const [_, searchOptions] = await Flat.getFlats();
  const queries = [
    SEARCH_QUERY_GENERIC,
    ...getSellHouseOptionsBySheetName(searchOptions, 'homePage').map(
      (o) => o.query
    ),
  ];

  const paths = queries.map((query) => ({
    params: {
      query,
    },
  }));

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps<
  StaticProps,
  StaticParams
> = async ({ params }) => {
  const offices = await Office.getOffices();
  const [flats, searchOptions] = await Flat.getFlats();
  const suggestions = suggestionUtils.getSuggestionsByQuery(params.query);

  const searchOption = searchOptions.find(
    (s) => slugifySearchQuery(s.text) === params.query
  );
  const searchOptionInPath = searchOption ? searchOption.text : '';
  const relevantFlats = computeResults(flats, {
    county: params.query === SEARCH_QUERY_GENERIC ? undefined : params.query,
  });

  const buyHouseOptions = computeBuyHouseOptions(searchOptions);
  const lastPosts = []; // TODO: await getLastPosts();
  const mapAreas = await computeMapAreas(flats);
  const testimonials = Testimonial.getTestimonials();

  return {
    props: {
      offices,
      buyHouseOptions,
      query: params.query,
      serializedFlats: Flat.serialize(relevantFlats),
      serializedSearchOptions: JSON.stringify(searchOptions),
      lastPosts,
      mapAreas,
      suggestions,
      searchOptionInPath: searchOptionInPath || 'Inmobiliaria Núcleo',
      testimonials,
    },
  };
};

export default HomePage;
